import React, { useMemo, forwardRef, useCallback, useState } from 'react'
import { getFileCover, buildAliCdnImg } from '@/utils/qiniu'

import { wrapperGallery } from '@/components/Gallery'
import classNames from 'classnames'

import styles from './index.less'

export const IMAGE_PLACEHOLDER_URL =
    'https://assets.sursung.com/activity/2024_imgs/dis/front/987998BDE85849DDB7FB6B717140C0591625153596828.jpg'

const defaultFallback =
    'https://assets.sursung.com/activity/2024_imgs/dis/front/13F83242300046AEBD16ABDA4C757AB51628046280157.jpg'

const ImgSon = ({
    src,
    width,
    height,
    imageStyle = {},
    previewSrc = '',
    fallback = defaultFallback,
    noFallBack = false,
    previewable,
    previewableUrlFn
}) => {
    const [status, setStatus] = useState('')
    const handelError = useCallback(
        e => {
            if (!noFallBack) {
                setStatus('error')
            }
        },
        [noFallBack]
    )

    const realSrc = useMemo(() => {
        if (status === 'error') {
            return fallback
        }
        return src
    }, [status, src, fallback])

    const realPreviewSrc = useMemo(() => {
        if (typeof previewableUrlFn === 'function') {
            return previewableUrlFn()
        }

        if (status === 'error') {
            return fallback
        }
        return previewSrc
    }, [previewableUrlFn, status, previewSrc, fallback])

    if (previewable) {
        return (
            <a href={realPreviewSrc} itemProp="contentUrl" data-log-text="查看图片">
                <img
                    style={imageStyle}
                    itemProp="thumbnail"
                    onError={handelError}
                    src={realSrc}
                    width={width}
                    height={height}
                    referrerPolicy="no-referrer"
                    alt=""
                />
            </a>
        )
    }

    return (
        <img
            style={{ cursor: 'auto', ...imageStyle }}
            itemProp="thumbnail"
            onError={handelError}
            src={realSrc}
            width={width}
            height={height}
            referrerPolicy="no-referrer"
            alt=""
        />
    )
}

export const Image = forwardRef(
    (
        {
            dataSource,
            multiple,
            size,
            noFallBack,
            fallback,
            len = 4,
            previewableUrlFn = null, // 自定义大图
            previewable = true,
            isClipping = true, // 是否需要经过阿里云将图片裁剪
            classnames,
            imageStyle,
            ...restProps
        },
        ref
    ) => {
        const curSize = typeof size === 'number' ? size : multiple ? 67 : 140

        let data = useMemo(() => {
            if (typeof dataSource === 'string') {
                return [dataSource].filter(v => typeof v === 'string' && !!v.trim())
            }

            return dataSource.filter(v => typeof v === 'string' && !!v.trim())
        }, [dataSource])

        if (len) {
            data = data.slice(0, len)
        }

        // 把某些erp带来的图片处理参数去掉
        if (Array.isArray(data)) {
            data = data.map(src => buildAliCdnImg(src))
        }

        const style = useMemo(() => {
            if (multiple) {
                return { width: `${curSize * 2 + 8}px`, minHeight: `${curSize * 2 + 8}px` }
            }

            return { width: `${curSize}px`, minHeight: `${curSize}px` }
        }, [curSize, multiple])

        return (
            <div
                ref={ref}
                className={classNames(styles.container, classnames)}
                style={isClipping ? style : {}}
                {...restProps}
            >
                {data.map((item, index) => (
                    <figure key={String(item) + String(index)} itemProp="associatedMedia" itemScope>
                        <ImgSon
                            previewSrc={item}
                            previewable={previewable}
                            src={isClipping ? getFileCover(item, { size: curSize * 2 }) : item}
                            width={curSize}
                            height={curSize}
                            imageStyle={imageStyle}
                            previewableUrlFn={previewableUrlFn}
                        />

                        <figcaption itemProp="caption description" />
                    </figure>
                ))}
            </div>
        )
    }
)

export default wrapperGallery(Image)
